import React, { useEffect, useState } from "react";
import { CardBody } from "reactstrap";
import withStyles from "isomorphic-style-loader/withStyles";
import styles from "../../styles.scss";
import SectionLabel from "../../common/sectionLabel";
import { Editor, EditorState, RichUtils } from "draft-js";
import "draft-js/dist/Draft.css";
import { LeftSidebarVariablesProp } from "../../common/types/variables";
import RTEEditorButtons from "../../common/rteEditorButtons";
import ImageSelectionInput from "../../common/imageSelectInput";
import RteLinkModal from "../../common/rteLinkModal";
import { CharityAssetsService } from "../../../../../../../../../services/charityAssetsService";
import { POST_DETAIL_BIO_DEFAULT_IMAGE } from "../constants";
import {
  createRteEditorState,
  ensureRteDecorator,
  getLinkEntityKeyAtSelection,
  isLinkActiveAtSelection,
  rteContentStateFromHtml,
  rteHtmlFromContentState,
  rteLinkDecorator,
} from "../../common/rteLinkUtils";

const PostDetailBioSection = ({
  variables,
  charityId,
  firebase,
  onError,
}: LeftSidebarVariablesProp) => {
  const [bioDescription, setBioDescription] = variables["bioDescription"];
  const [bioImage, setBioImage] = variables["bioImage"];
  const fetchBioImage = variables["fetchBioImage"] as (() => void) | undefined;
  const [hasLoadedHtml, setHasLoadedHtml] = useState(false);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkEntityKey, setLinkEntityKey] = useState<string | null>(null);

  const [editorState, setEditorState] = useState(() => {
    try {
      if (bioDescription) {
        return createRteEditorState(rteContentStateFromHtml(bioDescription));
      }
      return EditorState.createEmpty(rteLinkDecorator);
    } catch (error) {
      console.error("Error parsing bio HTML:", error);
      return EditorState.createEmpty(rteLinkDecorator);
    }
  });

  useEffect(() => {
    if (typeof fetchBioImage === "function") {
      fetchBioImage();
    }
  }, [fetchBioImage]);

  useEffect(() => {
    if (!hasLoadedHtml) {
      return;
    }
    const html = rteHtmlFromContentState(editorState.getCurrentContent());
    setBioDescription(html);
  }, [editorState, hasLoadedHtml, setBioDescription]);

  useEffect(() => {
    if (hasLoadedHtml || !bioDescription) {
      return;
    }
    setEditorState(
      createRteEditorState(rteContentStateFromHtml(bioDescription || ""))
    );
    setHasLoadedHtml(true);
  }, [bioDescription, hasLoadedHtml]);

  useEffect(() => {
    if (bioDescription === "" && !hasLoadedHtml) {
      setHasLoadedHtml(true);
    }
  }, [bioDescription, hasLoadedHtml]);

  const onEditorChange = (nextState: EditorState) => {
    setEditorState(ensureRteDecorator(nextState));
  };

  const toggleInlineStyle = (style: string) => {
    onEditorChange(RichUtils.toggleInlineStyle(editorState, style));
  };

  const toggleBlockType = (blockType: string) => {
    onEditorChange(RichUtils.toggleBlockType(editorState, blockType));
  };

  const isInlineStyleActive = (style: string) =>
    editorState.getCurrentInlineStyle().has(style);

  const isBlockTypeActive = (blockType: string) => {
    const selection = editorState.getSelection();
    const blockKey = selection.getStartKey();
    const currentBlock = editorState
      .getCurrentContent()
      .getBlockForKey(blockKey);
    return currentBlock.getType() === blockType;
  };

  const handleKeyCommand = (command: string, currentState: EditorState) => {
    const newState = RichUtils.handleKeyCommand(currentState, command);
    if (newState) {
      onEditorChange(newState);
      return "handled";
    }
    return "not-handled";
  };

  const openLinkModal = () => {
    setLinkEntityKey(getLinkEntityKeyAtSelection(editorState));
    setLinkModalOpen(true);
  };

  const handleBioImageUpload = async (file: File): Promise<string> => {
    if (!charityId) {
      throw new Error("Charity ID is required for bio image upload");
    }
    if (!firebase) {
      throw new Error("Firebase instance is required for bio image upload");
    }
    const assetsService = new CharityAssetsService(firebase);
    return assetsService.uploadAsset({
      charityId,
      assetType: "gallery",
      file,
      assetName: "post-detail-bio",
    });
  };

  return (
    <CardBody className={`${styles.cardBody} ${styles.postDetailSectionBody}`}>
      <ImageSelectionInput
        label={
          <SectionLabel
            label="Bio image"
            tooltip="Image shown above the bio text in the sidebar"
            subtitle="Min. size 400×270 px. PNG or JPEG."
          />
        }
        image={bioImage || ""}
        setImage={setBioImage}
        onUpload={handleBioImageUpload}
        defaultImage={POST_DETAIL_BIO_DEFAULT_IMAGE}
        altText="Bio sidebar image"
        minWidth={400}
        minHeight={270}
        onError={onError}
      />
      <div className={styles.section}>
        <SectionLabel
          label="Bio description"
          tooltip="Description shown in the bio sidebar"
          children={
            <RTEEditorButtons
              isInlineStyleActive={isInlineStyleActive}
              toggleInlineStyle={toggleInlineStyle}
              allowList={true}
              allowLink={true}
              isLinkActive={isLinkActiveAtSelection(editorState)}
              onLinkClick={openLinkModal}
              isBlockTypeActive={isBlockTypeActive}
              toggleBlockType={toggleBlockType}
            />
          }
        />
        <div className={styles.rteEditorSurface}>
          <Editor
            editorState={editorState}
            onChange={onEditorChange}
            handleKeyCommand={handleKeyCommand}
            placeholder="Start typing your bio content..."
          />
        </div>
      </div>
      <RteLinkModal
        isOpen={linkModalOpen}
        toggle={() => setLinkModalOpen(false)}
        editorState={editorState}
        entityKey={linkEntityKey}
        onComplete={onEditorChange}
      />
    </CardBody>
  );
};

export default withStyles(styles)(PostDetailBioSection);
